/* eslint-disable  @typescript-eslint/no-explicit-any */

import { Alert, Col, ProgressBar, Row, Table } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'components/base/Button';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Form } from 'react-bootstrap';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { PieChart, ScatterChart } from 'echarts/charts';
echarts.use([TooltipComponent, PieChart, ScatterChart]);

// fake data
//import { analysis_1 } from 'data/automatic_transcription/transcriptions';
import { EChartsOption } from 'echarts-for-react/lib/types';
//import { faCircle as faCircleEmpty } from '@fortawesome/free-regular-svg-icons';
import { faCircle as faCircleFull } from '@fortawesome/free-solid-svg-icons';
import React from 'react';

interface Outcome {
    conclusion: {
        title: string;
        body: string;
    };
    client_keyword_list: {
        ngram: string;
        frequency: number;
    }[];
    word_similarity: {
        score: number;
        score_text: string;
        advice: string;
        title: string;
        body: string;
        outcome: string;
        status: string;
    };
    open_questions: {
        score: number;
        score_text: string;
        advice: string;
        title: string;
        body: string;
        outcome: string;
        status: string;
    };
    closed_questions: {
        score: number;
        score_text: string;
        advice: string;
        title: string;
        body: string;
        outcome: string;
        status: string;
    };
    coach_talking: {
        score: number;
        score_text: string;
        advice: string;
        title: string;
        body: string;
        outcome: string;
        status: string;
    };
    coach_keyword_list: {
        ngram: string;
        frequency: number;
    }[];
    sentence_similarity: {
        score: number;
        score_text: string;
        advice: string;
        title: string;
        body: string;
        outcome: string;
        status: string;
    };
}

interface Transcript {
    transcript_id: number;
    outcome: Outcome;
    status: string;
}

const pieChart1Options: EChartsOption = (analysis: Transcript, getThemeColor: (name: string) => string) => ({
    color: [
        getThemeColor('info-300'),
        getThemeColor('warning-300'),
        getThemeColor('danger-300'),
        getThemeColor('success-300'),
        getThemeColor('primary')
    ],
    tooltip: {
        trigger: 'item'
    },
    responsive: true,
    maintainAspectRatio: false,

    series: [
        {
            name: 'Who is talking',
            type: 'pie',
            radius: ['50%', '90%'],
            startAngle: 0,
            avoidLabelOverlap: false,

            label: {
                show: false,
                position: 'center',
                formatter: '{x|{d}%} \n {y|{b}}',
                rich: {
                    x: {
                        fontSize: 31.25,
                        fontWeight: 800,
                        color: getThemeColor('gray-700'),
                        padding: [0, 0, 5, 15]
                    },
                    y: {
                        fontSize: 12.8,
                        color: getThemeColor('gray-700'),
                        fontWeight: 600
                    }
                }
            },
            emphasis: {
                label: {
                    show: true
                }
            },
            labelLine: {
                show: false
            },
            data: [
                { value: analysis['outcome']['coach_talking']['score'], name: 'Coach' },
                { value: 100 - analysis['outcome']['coach_talking']['score'], name: 'Client' }
            ]
        }
    ],
    grid: {
        bottom: 0,
        top: 0,
        left: 0,
        right: 0,
        containLabel: false
    }
});

const bubbleChartOptions: EChartsOption = (data: any[], getThemeColor: (name: string) => string) => ({
    grid: {
        left: '15%',
        right: '15%',
        top: '10%'
    },
    xAxis: {
        show: false,
        scale: true
    },
    yAxis: {
        show: false,
        scale: true
    },
    series: [
        {
            name: 'coach',
            data: data[0],
            type: 'scatter',
            symbolSize: function (data: number[]) {
                return 10 + data[2] * 100;
            },

            emphasis: {
                focus: 'series',
                label: {
                    show: true,
                    formatter: function (param: any) {
                        return param.data[3];
                    },
                    position: 'top'
                }
            },

            label: {
                show: true,
                formatter: function (param: any) {
                    return param.data[3];
                },
            },
            itemStyle: {
                color: getThemeColor('info-300')
            }
        },
        {
            name: 'client',
            data: data[1],
            type: 'scatter',
            symbolSize: function (data: number[]) {
                return 10 + data[2] * 100;
            },
            label: {
                show: true,
                formatter: function (param: any) {
                    return param.data[3];
                },
            },
            emphasis: {
                focus: 'series',
                label: {
                    show: true,
                    formatter: function (param: any) {
                        return param.data[3];
                    },
                    position: 'top'
                }
            },
            itemStyle: {
                color: getThemeColor('warning-300')
            }
        }
    ]
});



const ICFAnalysisView = ({
    analysis,
    className, onRefresh,
}: { analysis: string, className?: string, onRefresh?: () => void }) => {

    if (analysis === '') return (<>
        <Alert style={{ backgroundColor: '#d1e7dd' }}>
            <Alert.Heading className='text-body'>No feedback generated yet</Alert.Heading>
            <p className='text-body'>
                Request your feedback from “transcript”. If you have already done it, it will be available soon.
            </p>

            <Button type="submit" variant="primary" onClick={onRefresh} className="px-6 ms-auto">
                Refresh
            </Button>
        </Alert>
    </>)

    //console.log(analysis);
    const data: Transcript = JSON.parse(analysis);

    const { getThemeColor } = useAppContext();

    // Function to shuffle an array using Fisher-Yates algorithm
    function shuffleArray(array: number[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1)); // Generate random index
            [array[i], array[j]] = [array[j], array[i]]; // Swap elements
        }
        return array;
    }

    //const numbers = Array.from(Array(10).keys());
    // Shuffle the array
    const shuffledNumbers = shuffleArray(Array.from(Array(20).keys()));
    const shuffledNumbersX = shuffleArray(Array.from(Array(50).keys()));


    const bubble_data_for_normalization = [...data.outcome.coach_keyword_list.slice(0, 5), ...data.outcome.client_keyword_list.slice(0, 5)];
    // Extract "value" attribute from each object
    const values = bubble_data_for_normalization.map(obj => obj.frequency);

    // Calculate min and max values
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    const coach_bubble = data.outcome.coach_keyword_list.slice(0, 5).map((item, index) => (
        [shuffledNumbersX[index], // Assign consecutive x values
        shuffledNumbers[index], // Assign random y values (adjust range as needed)
        (item.frequency - minValue) / (maxValue - minValue),
        item.ngram,
            "coach"]
    ));

    const client_bubble = data.outcome.client_keyword_list.slice(0, 5).map((item, index) => (
        [shuffledNumbersX[index + 5], // Assign consecutive x values
        shuffledNumbers[index + 5], // Assign random y values (adjust range as needed)
        (item.frequency - minValue) / (maxValue - minValue),
        item.ngram,
            "client"]
    ));

    const data1 = [coach_bubble, client_bubble];

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        //const formData = new FormData(event.currentTarget);
        //const reflections = formData.get("reflections") as string;

        // store data to server
        // close modal


    }

    function getVariant(score: number) {
        // Logic to determine the variant based on the score
        if (score > 70) {
            return 'success';
        } else if (score > 50) {
            return 'warning';
        } else {
            return 'danger';
        }
    }

    return (
        <div className={classNames(className, 'w-100')}>

            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0" className="border-top border-300">
                    <Accordion.Header className="px-5 px-lg-6 bg-soft" >Who is talking?</Accordion.Header>
                    <Accordion.Body className="d-flex align-items-center mb-5">

                        <Col xs={12} xl={12} className="border-end border-300">
                            <Row className="g-0">
                                <ReactEChartsCore
                                    echarts={echarts}
                                    option={pieChart1Options(data, getThemeColor)}
                                    style={{ minHeight: '320px', width: '100%' }}
                                />
                            </Row>
                            <Row className="g-0">
                                <Col xs={12} xl={12} className="border-end border-300">
                                    <div className="px-5 px-lg-6 pt-1 bg-white">
                                        <strong>Coach: {data.outcome.coach_talking.score}% - Client: {100 - data.outcome.coach_talking.score}%</strong>
                                        <div className="d-flex align-items-center mb-0">
                                            <p>
                                                {data['outcome']["coach_talking"]["body"].replace("u2019", "'")}
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>What could be useful:</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            {data['outcome']["coach_talking"]["advice"].replace("u2019", "'")}
                                        </p>
                                    </div>
                                </div>

                            </Row>

                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>Note:</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            The role of the coach is to create space for client's reflection and new awareness, rather than providing ideas, advice,
                                            suggestions, direction, or lengthy reformulation. Allowing 70/80% of the session time for the client to speak and
                                            contemplate in silence would be ideal for a deep internal thought process.
                                            Pauses of one or two moments before asking the next question can also enhance the conversation.
                                            Short, clear questions are more effective than long, complex, or multiple questions.
                                        </p>
                                    </div>
                                </div>
                            </Row>

                        </Col>

                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            {/* Most used words */}
            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0" className="border-top border-300">
                    <Accordion.Header className="px-5 px-lg-6 bg-soft" >Most used words</Accordion.Header>
                    <Accordion.Body className="d-flex align-items-center mb-5">
                        <Col xs={12} xl={12} className="border-end border-300">
                            <Row className="g-0">
                                <ReactEChartsCore
                                    echarts={echarts}
                                    option={bubbleChartOptions(data1, getThemeColor)}
                                    style={{ minHeight: '320px', width: '100%' }}
                                />
                            </Row>
                            {/* Table of words */}
                            <Row className="g-0  justify-content-md-center p-2 bg-white">
                                <Col xs lg="2" xl="2" > </Col>
                                <Col xs="12" lg="12" xl="8">
                                    <Table bordered size='sm' className='bg-soft'>
                                        <thead>
                                            <tr>
                                                <th className="text-center w-50"><FontAwesomeIcon icon={faCircleFull} className="text-info fs-8 me-3 px-2" /> Coach</th>
                                                <th className="text-center w-50"><FontAwesomeIcon icon={faCircleFull} className="text-warning fs-8 me-3 px-2" /> Client</th>
                                            </tr>
                                        </thead>
                                    </Table>

                                    <Table bordered hover size='sm' className='bg-soft'>
                                        <thead>
                                            <tr>
                                                <th className="text-center w-50">Word</th>
                                                <th className="text-center w-50">Frequency</th>
                                            </tr>
                                        </thead>
                                        <tbody>

                                            {/* Render table rows */}
                                            {data.outcome.coach_keyword_list.slice(0, 5).map((row, rowIndex) => (
                                                <tr key={"coach_" + rowIndex}>
                                                    <td> <FontAwesomeIcon icon={faCircleFull} className="text-info fs-8 me-3 px-2" /> {row.ngram}</td>
                                                    <td className="text-center ">{row.frequency}</td>
                                                </tr>
                                            ))}
                                            {/* Render table rows */}
                                            {data.outcome.client_keyword_list.slice(0, 5).map((row, rowIndex) => (
                                                <tr key={"client_" + rowIndex}>
                                                    <td ><FontAwesomeIcon icon={faCircleFull} className="text-warning fs-8 me-3 px-2" /> {row.ngram}</td>
                                                    <td className="text-center">{row.frequency}</td>
                                                </tr>
                                            ))}

                                        </tbody>
                                    </Table>
                                </Col>
                                <Col xs lg="2" xl="2" > </Col>
                            </Row>

                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>Same words {data.outcome.word_similarity.score}%</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            {data.outcome.word_similarity.body.replace(/u2019/g, "'")}
                                        </p>
                                    </div>
                                </div>
                            </Row>
                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>What could be useful:</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            {data.outcome.word_similarity.advice.replace(/u2019/g, "'")}
                                        </p>
                                    </div>
                                </div>
                            </Row>
                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>Note:</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            Using the same words as your client helps build rapport and trust, as the client is more likely to feel heard and understood.
                                            Additionally, it helps identify the client's value words and key words relevant to their self-discovery.
                                            The feedback report shows where you put your attention in coaching conversations.
                                            Filler words, such as "yes," "ok," and "um," are also considered in your coaching feedback report.
                                            When one of your most frequent words is a filler word, practicing clean language and getting rid of the habit can be beneficial.
                                            When the client's answers are very long, some words may be repeated several times even if not relevant.
                                        </p>
                                    </div>
                                </div>
                            </Row>
                        </Col>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            {/* Commn language (Sentence Similarity) */}
            <Accordion>
                <Accordion.Item eventKey="0" className="border-top border-300">
                    <Accordion.Header className="px-5 px-lg-6 bg-soft" >Common language</Accordion.Header>
                    <Accordion.Body className="d-flex align-items-center mb-5">
                        <Col xs={12} xl={12} className="border-end border-300">
                            <Row className="g-0 mx-8 bg-white">
                                <ProgressBar striped variant={getVariant(data.outcome.sentence_similarity.score)}
                                    now={data.outcome.sentence_similarity.score} className='m-3' style={{ height: '15px' }} />
                            </Row>
                            <Row className="g-0 mx-8 bg-white">
                                <strong className='text-center fs-5'> {data.outcome.sentence_similarity.score}%</strong>
                            </Row>
                            <Row className="g-0">
                                <Col xs={12} xl={12} className="border-end border-300">
                                    <div className="px-5 px-lg-6 pt-1 bg-white">
                                        <strong>Coach and client language similarity {data.outcome.sentence_similarity.score}%</strong>
                                        <div className="d-flex align-items-center mb-0">
                                            <p>
                                                {data.outcome.sentence_similarity.body.replace(/u2019/g, "'")}
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>What could be useful:</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            {data.outcome.sentence_similarity.advice.replace(/u2019/g, "'")}
                                        </p>
                                    </div>
                                </div>
                            </Row>

                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>Note:</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            It is important for a coach to pay attention to the client's speech characteristics in terms of
                                            vocabulary, word order, grammar, sentence structure, idiomatic expressions, and use of metaphors.
                                            Matching the client's language patterns is an important factor in effective communication.
                                            The report result is a key indicator of the degree to which a coach
                                            listens actively and lets go of personal interference. Common words and common language are two
                                            different criteria and results may differ.
                                        </p>
                                    </div>
                                </div>
                            </Row>

                        </Col>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>


            {/* Open-ended question */}
            <Accordion>
                <Accordion.Item eventKey="0" className="border-top border-300">
                    <Accordion.Header className="px-5 px-lg-6 bg-soft" >Open-endend questions</Accordion.Header>
                    <Accordion.Body className="d-flex align-items-center mb-5">
                        <Col xs={12} xl={12} className="border-end border-300">

                            <Row className="g-0 mx-8 bg-white">
                                <ProgressBar striped variant={getVariant(data.outcome.open_questions.score)}
                                    now={data.outcome.open_questions.score} className='m-3' style={{ height: '15px' }} />
                            </Row>
                            <Row className="g-0 mx-8 bg-white">
                                <strong className='text-center fs-5'> {data.outcome.open_questions.score}%</strong>
                            </Row>

                            <Row className="g-0">
                                <Col xs={12} xl={12} className="border-end border-300 bg-100">
                                    <div className="px-5 px-lg-6 pt-1 bg-white">
                                        <strong>Coach and client language similarity {data.outcome.open_questions.score}%</strong>
                                        <div className="d-flex align-items-center mb-0">
                                            <p>
                                                {data.outcome.open_questions.body.replace(/u2019/g, "'")}
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>What could be useful:</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            {data.outcome.open_questions.advice.replace(/u2019/g, "'")}
                                        </p>
                                    </div>
                                </div>
                            </Row>
                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>Note:</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            Open-ended questions lead to a more meaningful conversation and deeper insights into the
                                            client's beliefs, values, attitudes, and experiences. Though there is not a set number, closed-
                                            ended questions should only be used when necessary, and the coach should primarily ask open-
                                            ended questions.
                                        </p>
                                    </div>
                                </div>
                            </Row>
                        </Col>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            {/* Conclusion */}
            <Accordion>
                <Accordion.Item eventKey="0" className="border-top border-300">
                    <Accordion.Header className="px-5 px-lg-6 bg-soft" >Conclusion</Accordion.Header>
                    <Accordion.Body className="d-flex align-items-center mb-5">
                        <Col xs={12} xl={12} className="border-end border-300">
                            <Row className="g-0">
                                <Col xs={12} xl={12} className="border-end border-300 bg-100">
                                    <div className="px-5 px-lg-6 pt-1 bg-white">
                                        <div className="d-flex align-items-center mb-0">
                                            <p>
                                                {data.outcome.conclusion.body.replace(/u2019/g, "'").replace(/<br\s*?>/gi, '\n').split('\n').map((line, index) => (
                                                    <React.Fragment key={index}>
                                                        {line}
                                                        <br />
                                                    </React.Fragment>
                                                ))}
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="g-0">
                                <div className="px-5 px-lg-6 pt-1 bg-white">
                                    <strong>Note:</strong>
                                    <div className="d-flex align-items-center mb-0">
                                        <p>
                                            Your feedback report shows how you demonstrate different coaching skills, and at the same
                                            time, it calls attention to the alignment between your intentions and the actual results. Starting
                                            from your third report, the 'trends and practice progress' graphic becomes available, showing
                                            your progress in each area with one specific client. When the trends graphic of one particular
                                            client differs substantially from all the others, it could be useful to identify what in the coaching
                                            relationship represents a challenge for you. It could be: the topics explored, client's values, the context.
                                            All criteria are connected, with practice you will learn to observe them individually and as a whole.
                                        </p>
                                    </div>
                                </div>
                            </Row>
                        </Col>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <Row className="g-0 mt-3 px-5 px-lg-6 mb-6">
                <strong>My reflections</strong>
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Control as="textarea" rows={3} name="reflections" id="reflections" />
                    </Form.Group>
                    <div className="d-flex gap-1">
                        <Button type="submit" variant="warning" className="px-6 ms-auto">
                            Save
                        </Button>
                    </div>
                </Form>
            </Row>
        </div>
    );
};

export default ICFAnalysisView;
